import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

import { bookingsService } from "../../services/bookings.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";
import {
  BOOKING_STORAGE_KEY,
  BookingHeader,
  BookingInfoCards,
  BookingSummary,
  FlightHeader,
  PAX_TYPES,
  PRIMARY,
  bookingCss,
  formatINR,
  loadDraft,
} from "../../components/flight-booking/BookingShared";

const PAYMENT_METHODS = [
  // { value: "gateway", label: "Payment Gateway", icon: "bi-credit-card-2-front", desc: "UPI, debit / credit cards and net banking" },
  { value: "wallet", label: "Wallet", icon: "bi-wallet2", desc: "Pay from your wallet balance" },
];

const FEMALE_TITLES = ["Mrs", "Ms", "Miss"];

// Build one CreateBookingPayload passenger from a traveller entered on the details step.
// Gender is the one chosen on the details step (auto-selected from the title); falls back to the title for older drafts.
const toPassengerPayload = (p) => ({
  pax_type: p.type.fareKey,
  title: p.title,
  first_name: [p.first.trim(), p.middle?.trim()].filter(Boolean).join(" "),
  last_name: p.last.trim(),
  gender: p.gender || (FEMALE_TITLES.includes(p.title) ? "female" : "male"),
  dob: p.dob,
  nationality: p.nationality || "",
  wheelchair_required: p.wheelchair == "Yes",
  // Passport details are only sent when the traveller's passport section was ticked
  passport_number: p.has_passport ? p.passport_number?.trim() || "" : "",
  passport_issue_date: p.has_passport ? p.passport_issue_date || "" : "",
  passport_expiry: p.has_passport ? p.passport_expiry || "" : "",
  passport_issuing_country: p.has_passport ? p.passport_issuing_country || "" : "",
});

const FlightReview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const draft = location.state?.passengers ? location.state : loadDraft();
  const { fare, search, passengers } = draft || {};
  const pax = draft?.pax || search?.pax_detail;

  const [rulesOpen, setRulesOpen] = useState(false);
  const [method, setMethod] = useState("wallet");
  const [confirmed, setConfirmed] = useState(false);
  const [booking, setBooking] = useState(false);

  // Nothing to review (direct visit or storage cleared): go back to the start
  if (!fare || !passengers?.length || !pax) return <Navigate to="/flight-search" replace />;

  // const total = (fare.fare?.total_fare || 0) *
  //   PAX_TYPES.reduce((count, t) => count + (pax[t.key] || 0), 0);
  const total = (fare.fare?.total?.total_fare || 0);

  const handlePay = async () => {    
    if (booking) return;
    const result = await withReactContent(Swal).fire({
      title: "Confirm booking?",
      text: `Pay ${formatINR(total)} and book this ticket for ${passengers.length} passenger${passengers.length == 1 ? "" : "s"}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, book ticket",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setBooking(true);
      const res = await bookingsService.create({
        fare_reference_code: fare.fare_reference_code,
        passengers: passengers.map(toPassengerPayload),
      });
      if (res?.success) {
        const created = res.resData;
        try {
          sessionStorage.removeItem(BOOKING_STORAGE_KEY);
        } catch (err) {
          // storage unavailable: nothing to clear
        }
        toast.success(res.message || "Booking created successfully");
        navigate("/booking-details/" + created.id, { replace: true });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      withReactContent(Swal).fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="fb-page">
      <style>{bookingCss}</style>
      <style>{css}</style>

      <BookingHeader fare={fare} pax={pax} step={2} onBack={() => navigate("/flight-booking", { state: draft })} />

      <div className="row g-3 mt-0">
        <div className="col-12 col-lg-8">
          <FlightHeader fare={fare} search={search} />
          <BookingInfoCards fare={fare} />

          {/* Passenger confirmation */}
          <div className="fb-card">
            <div className="fb-section-head" style={{ margin: "0 0 6px" }}><i className="bi bi-people"></i> Passenger Confirmation</div>
            {passengers.map((p) => (
              <div className="fr2-pax" key={p.id}>
                <div>
                  <div className="fb-strong">{[p.title, p.first.trim(), p.middle?.trim(), p.last.trim()].filter(Boolean).join(" ")}</div>
                  <div className="fb-muted">{[p.type.label, p.dob && `DOB ${new Date(p.dob).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`, p.wheelchair == "Yes" && "Wheelchair required"].filter(Boolean).join(" · ")}</div>
                </div>
                <i className="bi bi-check-lg fr2-ok"></i>
              </div>
            ))}
            {draft.gst && (
              <div className="fr2-pax">
                <div>
                  <div className="fb-strong">GST: {draft.gst.number}</div>
                  <div className="fb-muted">{draft.gst.company}</div>
                </div>
                <i className="bi bi-check-lg fr2-ok"></i>
              </div>
            )}
          </div>

          {/* Fare rules */}
          {/* <div className="fb-card p-0">
            <button type="button" className="fr2-collapse" aria-expanded={rulesOpen} onClick={() => setRulesOpen(!rulesOpen)}>
              <span><i className="bi bi-file-earmark-text me-2"></i>Fare Rules</span>
              <i className={`bi bi-chevron-${rulesOpen ? "up" : "down"}`}></i>
            </button>
            {rulesOpen && (
              <div className="fr2-rules">
                <div className="fb-strong mb-1">{fare.is_refundable ? "Refundable fare" : "Non-refundable fare"}</div>
                <div className="fb-muted mb-2">{fare.fare_rule || "No fare rules provided for this fare."}</div>
                {fare.baggage_policy && <div className="fb-muted"><i className="bi bi-suitcase2 me-1"></i>Baggage: {fare.baggage_policy}</div>}
              </div>
            )}
          </div> */}

          {/* Payment method */}
          <div className="fb-card">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div className="fr2-card-title mb-0">Payment Method</div>
              {/* <span className="fr2-warn">PAYMENT NOT CONNECTED</span> */}
            </div>
            <div className="row g-3" role="radiogroup" aria-label="Payment method">
              {PAYMENT_METHODS.map((m) => (
                <div className="col-12 col-md-6" key={m.value}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={method == m.value}
                    className={`fr2-method ${method == m.value ? "active" : ""}`}
                    onClick={() => setMethod(m.value)}
                  >
                    <span className="d-flex justify-content-between align-items-center mb-2">
                      <span className={`fr2-radio ${method == m.value ? "on" : ""}`} />
                      <i className={`bi ${m.icon}`}></i>
                    </span>
                    <span className="fb-strong d-block">{m.label}</span>
                    <span className="fb-muted d-block">{m.value == "wallet" ? "Wallet balance is not available here yet" : m.desc}</span>
                  </button>
                </div>
              ))}
            </div>

            <label className="fr2-confirm">
              <input type="checkbox" className="form-check-input m-0" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              <span>I confirm passenger names, travel dates and GST details are correct, and have reviewed the fare rules.</span>
            </label>

            <button type="button" className="fb-primary fr2-pay" disabled={!confirmed || booking} onClick={handlePay}>
              {booking ? "Booking..." : <>Pay {formatINR(total)} &amp; Book Ticket <i className="bi bi-arrow-right ms-1"></i></>}
            </button>
            <div className="fb-muted mt-2"><i className="bi bi-shield-check me-1"></i>Booking and ticket issuance follow successful payment confirmation.</div>
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <BookingSummary fare={fare} search={search} pax={pax}>
            <button type="button" className="fb-goback" onClick={() => navigate("/flight-booking", { state: draft })}>
              <i className="bi bi-arrow-left me-2"></i>Go Back
            </button>
          </BookingSummary>
        </div>
      </div>
    </div>
  );
};

const css = `
.fr2-eyebrow { font-size:10px; font-weight:700; letter-spacing:.8px; color:${PRIMARY}; }
.fr2-heading { font-size:24px; font-weight:700; color:#0f172a; margin:2px 0 4px; }
.fr2-card-title { font-size:16px; font-weight:700; color:#0f172a; margin-bottom:12px; display:flex; align-items:center; gap:8px; }
.fr2-pax { display:flex; justify-content:space-between; align-items:center; padding:10px 0; border-top:1px solid #eef0f5; }
.fr2-ok { color:#15803d; font-size:18px; }
.fr2-collapse { width:100%; display:flex; justify-content:space-between; align-items:center; border:none; background:none; padding:14px 18px; font-size:14px; font-weight:600; color:#0f172a; }
.fr2-rules { padding:0 18px 16px; border-top:1px solid #eef0f5; padding-top:12px; }
.fr2-warn { font-size:10px; font-weight:700; letter-spacing:.4px; color:#c2410c; background:#fff4e5; border:1px solid #fed7aa; border-radius:999px; padding:3px 10px; }
.fr2-method { width:100%; text-align:left; background:#fff; border:1px solid #e5e7eb; border-radius:10px; padding:14px 16px; }
.fr2-method:hover { border-color:#9db3d9; }
.fr2-method.active { border-color:${PRIMARY}; background:#f3f7ff; }
.fr2-method i { font-size:18px; color:${PRIMARY}; }
.fr2-radio { width:14px; height:14px; border-radius:50%; border:2px solid #64748b; position:relative; display:inline-block; }
.fr2-radio.on { border-color:${PRIMARY}; }
.fr2-radio.on::after { content:""; position:absolute; inset:2px; border-radius:50%; background:${PRIMARY}; }
.fr2-confirm { display:flex; gap:10px; align-items:flex-start; font-size:13px; color:#0f172a; margin:16px 0 14px; cursor:pointer; }
.fr2-confirm input { margin-top:3px !important; flex-shrink:0; }
.fr2-pay { width:100%; margin:0; height:44px; }
.fr2-pay:disabled { opacity:.55; cursor:not-allowed; }
.fr2-method:focus-visible, .fr2-collapse:focus-visible { outline:2px solid ${PRIMARY}; outline-offset:2px; }
`;

export default FlightReview;
